import * as React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Form as FinalForm, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import createDecorator from 'final-form-calculate';
import { Form, Button, Grid } from 'semantic-ui-react';
import { v4 as uuidV4 } from 'uuid';
import moment from 'moment';
import set from 'lodash.set';

// Types
// TODO
// import { AppState } from 'services/types';

// Components
// @ts-ignore
import ErrorAlert from 'components/ErrorAlert';
// @ts-ignore
import WorkTypeOptions from 'components/WorkTypeOptions';

// Form
// @ts-ignore
import { normalizers, validators, RenderField, RenderDropdown } from 'forms';

// Utils
import { calculateTotalPaidHours } from 'utils/timesheets';
import hoursToDuration from 'utils/hours-to-duration';

// Components
import SubmittingSection from 'components/SubmissionSection';
// @ts-ignore
import TimeOptions from '../../../../../../../../components/TimeOptions';

// Selectors
// @ts-ignore
import { selectInitialValues } from './selectors';

// Normalizers
const { toNull } = normalizers;

// Validators
const { composeValidators, isRequired, isDuration } = validators;

// Constants
const NAMESPACE = 'TIMESHEET_TIMESHEET_ENTRIES_MANAGE';

const updates = (_value: any, name: string, allValues: any): any => {
    const { autoCalculateHours, startTime, endTime, duplicates } = allValues;

    const duplicatesLength = duplicates.length;

    const totalHours = calculateTotalPaidHours({ startTime, endTime });

    if (autoCalculateHours) {
        if (name !== 'duplicates' || !duplicates[duplicatesLength - 1].hours) {
            const quotient =
                Math.floor((totalHours * 4) / duplicatesLength) / 4;
            const remainder = ((totalHours * 4) % duplicatesLength) / 4;
            return {
                duplicates: duplicates.map(
                    (duplicate: any, index: number): any => ({
                        ...duplicate,
                        hours:
                            index === duplicatesLength - 1
                                ? quotient + remainder
                                : quotient
                    })
                )
            };
        }
    }

    return {};
};

const calculator = createDecorator(
    {
        field: 'startTime',
        updates
    },
    {
        field: 'endTime',
        updates
    },
    {
        field: 'duplicates',
        updates
    },
    {
        field: /\.hours/,
        updates: (_value, _name, allValues: any): any => {
            const { startTime, endTime } = allValues;

            const totalHours = calculateTotalPaidHours({ startTime, endTime });

            const totalHoursInDuplicates = allValues.duplicates.reduce(
                (accumulator: number, currentValue: any): number =>
                    accumulator + currentValue.hours,
                0
            );

            if (totalHours !== totalHoursInDuplicates) {
                return {
                    autoCalculateHours: false
                };
            }

            return {};
        }
    }
);

export interface OwnProps {
    timesheetEntryUuid?: string;
    onClose: () => void;
    insertRows: Function;
}

interface ConnectedProps {
    initialValues?: any;
}

type Props = OwnProps & ConnectedProps;

class TimesheetEntrySpitForm extends React.PureComponent<Props> {
    handleSubmit = (values: any): void => {
        const { onClose, insertRows } = this.props;
        const { startTime, duplicates } = values;

        const momentStartTime = moment(startTime, 'HH:mm');

        const formattedDuplicates = duplicates.map(
            ({ hours, ...value }: any): any => ({
                ...value,
                startTime: momentStartTime.format('HH:mm'),
                endTime: momentStartTime.add(hours, 'hours').format('HH:mm')
            })
        );

        insertRows(formattedDuplicates);
        onClose();
    };

    render(): React.ReactElement {
        const { onClose, initialValues } = this.props;

        return (
            <React.Fragment>
                <FinalForm
                    onSubmit={this.handleSubmit}
                    decorators={[calculator]}
                    mutators={{
                        ...arrayMutators
                    }}
                    initialValues={initialValues}
                    subscription={{}}
                    validate={(values: any): any => {
                        const errors = {};

                        const { startTime, duplicates = [] } = values;

                        let hour = moment(startTime, 'HH:mm').hours();

                        // Check if entry is being spit into the next day
                        duplicates.forEach(
                            (value: any, index: number): void => {
                                let duration = value.hours;
                                if (Number(value.hours)) {
                                    duration = hoursToDuration(duration, true)
                                }

                                if (isDuration(duration)) {
                                    set(
                                            errors,
                                            ['duplicates', index, 'hours'],
                                            isDuration(duration)
                                        );
                                }
                                else {
                                    const [hours, mins] = duration.split(':').map(parseFloat);

                                    if (mins % 15) {
                                        set(
                                            errors,
                                            ['duplicates', index, 'hours'],
                                            'Must be multiples of 15 mins'
                                        );
                                    }

                                    hour += Number(hours);

                                    if (hours > 24) {
                                        set(
                                            errors,
                                            ['duplicates', index, 'hours'],
                                            "Can't spit entry past midnight"
                                        );
                                    }
                                }
                            }
                        );

                        return errors;
                    }}
                    render={(props: any): React.ReactElement => {
                        const {
                            handleSubmit,
                            form: {
                                mutators: { push }
                            }
                        } = props;
                        return (
                            <Form autoComplete="off" onSubmit={handleSubmit}>
                                <Grid>
                                    <Grid.Row>
                                        <Grid.Column
                                            mobile={16}
                                            tablet={8}
                                            computer={8}
                                        >
                                            <Field
                                                name="endTime"
                                                subscription={{ value: true }}
                                            >
                                                {({
                                                    input: { value }
                                                }: any): React.ReactElement => (
                                                    <TimeOptions
                                                        isStart
                                                        endTime={value}
                                                    >
                                                        {(
                                                            options: any
                                                        ): React.ReactElement => (
                                                            <Field
                                                                component={
                                                                    RenderDropdown as any
                                                                }
                                                                name="startTime"
                                                                subscription={{
                                                                    value: true,
                                                                    touched:
                                                                        true,
                                                                    error: true
                                                                }}
                                                                options={
                                                                    options
                                                                }
                                                                label="Start Time"
                                                                placeholder="Enter start time"
                                                                parse={toNull}
                                                                fluid
                                                                search
                                                                selection
                                                                selectOnBlur={
                                                                    false
                                                                }
                                                                validate={
                                                                    isRequired
                                                                }
                                                            />
                                                        )}
                                                    </TimeOptions>
                                                )}
                                            </Field>
                                        </Grid.Column>
                                        <Grid.Column
                                            mobile={16}
                                            tablet={8}
                                            computer={8}
                                        >
                                            <Field
                                                name="startTime"
                                                subscription={{ value: true }}
                                            >
                                                {({
                                                    input: { value }
                                                }: any): React.ReactElement => (
                                                    <TimeOptions
                                                        isEnd
                                                        startTime={value}
                                                    >
                                                        {(
                                                            options: any
                                                        ): React.ReactElement => (
                                                            <Field
                                                                component={
                                                                    RenderDropdown as any
                                                                }
                                                                name="endTime"
                                                                subscription={{
                                                                    value: true,
                                                                    touched:
                                                                        true,
                                                                    error: true
                                                                }}
                                                                options={
                                                                    options
                                                                }
                                                                label="End Time"
                                                                placeholder="Enter end time"
                                                                parse={toNull}
                                                                fluid
                                                                search
                                                                selection
                                                                selectOnBlur={
                                                                    false
                                                                }
                                                                validate={
                                                                    isRequired
                                                                }
                                                            />
                                                        )}
                                                    </TimeOptions>
                                                )}
                                            </Field>
                                        </Grid.Column>
                                    </Grid.Row>
                                    <Grid.Row>
                                        <FieldArray name="duplicates">
                                            {({
                                                fields
                                            }): React.ReactElement[] => {
                                                return fields.map(
                                                    (
                                                        name: string,
                                                        index: number
                                                    ): React.ReactElement => (
                                                        <React.Fragment
                                                            key={name}
                                                        >
                                                            <Grid.Column
                                                                width={8}
                                                            >
                                                                <WorkTypeOptions
                                                                    useCompositeKey
                                                                >
                                                                    {(
                                                                        options: any
                                                                    ): React.ReactElement => (
                                                                        <Field
                                                                            name={`${name}.workTypeCompositeKey`}
                                                                            label={
                                                                                index
                                                                                    ? undefined
                                                                                    : 'Task'
                                                                            }
                                                                            placeholder="Add task"
                                                                            parse={
                                                                                toNull
                                                                            }
                                                                            fluid
                                                                            component={
                                                                                RenderDropdown as any
                                                                            }
                                                                            subscription={{
                                                                                value: true,
                                                                                touched:
                                                                                    true,
                                                                                error: true
                                                                            }}
                                                                            options={
                                                                                options
                                                                            }
                                                                            search
                                                                            selection
                                                                            closeOnChange
                                                                            upward={
                                                                                false
                                                                            }
                                                                            clearable
                                                                            selectOnBlur={
                                                                                false
                                                                            }
                                                                            nullOnUnmount={
                                                                                false
                                                                            }
                                                                        />
                                                                    )}
                                                                </WorkTypeOptions>
                                                            </Grid.Column>
                                                            <Grid.Column
                                                                width={8}
                                                            >
                                                                <Field
                                                                    name={`${name}.hours`}
                                                                    component={
                                                                        RenderField as any
                                                                    }
                                                                    subscription={{
                                                                        value: true,
                                                                        touched:
                                                                            true,
                                                                        error: true
                                                                    }}
                                                                    label={
                                                                        index
                                                                            ? undefined
                                                                            : 'Hours'
                                                                    }
                                                                    placeholder="Enter hours"
                                                                    validate={composeValidators(
                                                                        isRequired
                                                                    )}
                                                                    nullOnUnmount={
                                                                        false
                                                                    }
                                                                    format={(value) => {
                                                                        if (Number(value)) {
                                                                            const duration = hoursToDuration(value, true)
                                                                            return duration
                                                                        }
                                                                        return value
                                                                    }}
                                                                />
                                                            </Grid.Column>
                                                        </React.Fragment>
                                                    )
                                                );
                                            }}
                                        </FieldArray>
                                    </Grid.Row>
                                    <Grid.Row>
                                        <Grid.Column
                                            width={16}
                                            textAlign="center"
                                        >
                                            <Button
                                                type="button"
                                                primary
                                                circular
                                                onClick={(): void =>
                                                    push('duplicates', {
                                                        ...initialValues.original,
                                                        unpaidBreakMinutes:
                                                            undefined,
                                                        paidBreakMinutes:
                                                            undefined,
                                                        uuid: uuidV4()
                                                    })
                                                }
                                            >
                                                Add entry
                                            </Button>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>
                                <ErrorAlert namespace={NAMESPACE} />
                                <SubmittingSection
                                    submittingButtonParam={{
                                        namespace: NAMESPACE
                                    }}
                                    normalCancelButtonParam={{
                                        onClose: (): void => {
                                            onClose();
                                        }
                                    }}
                                />
                            </Form>
                        );
                    }}
                />
            </React.Fragment>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    any, // AppState,
    OwnProps,
    ConnectedProps
>({
    initialValues: selectInitialValues
});

export default connect(mapStateToProps)(TimesheetEntrySpitForm);
